import React, { useMemo, useState } from 'react'
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import { styled } from '@pergas-common/pergas-components'

const FILTER_MODE_OR = 'or'
const FILTER_MODE_AND = 'and'

const HeaderHolder = styled.div`
  display: inline-flex;
  align-items: center;
`

const Body = styled.div`
  width: 280px;
  padding: 8px;
`

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
`

const ColumnMultiSelectFilter = ({
  title,
  options = [],
  selectedValues = [],
  selectedMatchMode = FILTER_MODE_OR,
  showMatchMode = false,
  onApply,
  locale
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [search, setSearch] = useState('')
  const [draftSelection, setDraftSelection] = useState([])
  const [draftMatchMode, setDraftMatchMode] = useState(selectedMatchMode)

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (query === '') {
      return options
    }
    return options.filter((value) => value.toLowerCase().includes(query))
  }, [options, search])

  const open = Boolean(anchorEl)

  const onOpen = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDraftSelection(selectedValues)
    setDraftMatchMode(selectedMatchMode)
    setSearch('')
    setAnchorEl(event.currentTarget)
  }

  const onClose = () => {
    setAnchorEl(null)
  }

  const toggleDraftValue = (value) => {
    setDraftSelection((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value)
      }
      return [...prev, value]
    })
  }

  const apply = () => {
    if (typeof onApply === 'function') {
      onApply(draftSelection, showMatchMode ? draftMatchMode : FILTER_MODE_OR)
    }
    onClose()
  }

  return (
    <>
      <HeaderHolder>
        <span>{title}</span>
        <IconButton
          size='small'
          onClick={onOpen}
          title={locale?.filter || title}
        >
          <FilterListIcon
            fontSize='inherit'
            style={{ color: selectedValues.length > 0 ? '#64B5F6' : '#FFFFFF' }}
          />
        </IconButton>
      </HeaderHolder>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableEnforceFocus
        disableAutoFocus
      >
        <Body onClick={(e) => e.stopPropagation()}>
          <TextField
            fullWidth
            size='small'
            label={locale?.search || 'Search'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {showMatchMode && (
            <>
              <FormLabel component='legend' style={{ marginTop: 8 }}>{locale?.match_mode || 'Match mode'}</FormLabel>
              <RadioGroup
                row
                value={draftMatchMode}
                onChange={(e) => setDraftMatchMode(e.target.value)}
              >
                <FormControlLabel value={FILTER_MODE_OR} control={<Radio size='small' />} label={locale?.any || 'Any'} />
                <FormControlLabel value={FILTER_MODE_AND} control={<Radio size='small' />} label={locale?.all || 'All'} />
              </RadioGroup>
            </>
          )}
          <FormGroup style={{ maxHeight: 240, overflowY: 'auto' }}>
            {filteredOptions.map((value) => (
              <FormControlLabel
                key={value}
                control={<Checkbox size='small' checked={draftSelection.includes(value)} onChange={() => toggleDraftValue(value)} />}
                label={value}
              />
            ))}
          </FormGroup>
          <Actions>
            <Button size='small' onClick={() => setDraftSelection([])}>{locale?.clear || 'Clear'}</Button>
            <Button size='small' variant='contained' onClick={apply}>{locale?.apply || 'Apply'}</Button>
          </Actions>
        </Body>
      </Popover>
    </>
  )
}

export default ColumnMultiSelectFilter
